import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from '@mui/material/FormControl';
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import {styled} from "@mui/material/styles";
import ForgotPassword from "./ForgotPassword";
import ColorModeSelect from "@/theme/ColorModeSelect";
import {useForm} from "react-hook-form";
import {AccountCircleOutlined, PasswordOutlined} from "@mui/icons-material";
import {useAuthInfo, useSignin, useToken} from "@/store/account.c";
import md5 from "md5";
import AppTheme from "@/theme/AppTheme";
import {inputsCustomizations} from "@/theme/customizations/inputs";
import {useQueryCaptcha} from "@/service/account.s";
import {FormLabel} from "@mui/material";
import {zodResolver} from "@hookform/resolvers/zod";
import * as z from "zod";
import {useEffect} from "react";
import {useRouter} from "@/helper/hooks";
import {EmailOutlined, CropFreeOutlined} from '@mui/icons-material';

const CardContainer = styled(MuiCard)(({theme}) => ({
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    width: "100%",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    margin: "auto",
    borderRadius: '24px',
    // border: '1px solid',
    // borderColor: 'hsla(220, 20%, 65%, 0.4)',
    [theme.breakpoints.up("sm")]: {
        maxWidth: "460px",
    },
    boxShadow:
        "0 1px 1px #8592ad33",
    ...theme.applyStyles("dark", {
        boxShadow:
            "0 1px 1px #8592ad33",
    }),
}));

const SigninContainer = styled(Stack)(() => ({
    height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
    minHeight: "100%",
    // padding: theme.spacing(2),
    position: 'relative',
    '&::before': {
        content: '""',
        background: 'radial-gradient(#d2f1df, #d3d7fa, #bad8f4)',
        backgroundSize: '400% 400%',
        animation: 'gradient 15s ease infinite',
        position: 'absolute',
        height: '100%',
        width: '100%',
        opacity: '0.3',
    },

    // "&::before": {
    //     content: '""',
    //     display: "block",
    //     position: "absolute",
    //     zIndex: -1,
    //     inset: 0,
    //     backgroundImage:
    //         "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    //     backgroundRepeat: "no-repeat",
    //     ...theme.applyStyles("dark", {
    //         backgroundImage:
    //             "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    //     }),
    // },
}));

const validationSchema = z.object({
    account: z.string({message: '请填写账号'}).nonempty({message: '请填写账号'}),
    password: z.string({message: "请填写密码"}).nonempty({message: "请填写密码"}),
    captcha: z.string({message: "请填写验证码"}).nonempty({message: "请填写验证码"}),
});

export type SigninForm = z.infer<typeof validationSchema>

export default function SigninPage() {
    const resolver = zodResolver(validationSchema);
    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<SigninForm>({
        resolver,
    });
    const {signinPending, signin} = useSignin();
    const token: string = useToken()
    const router = useRouter();
    const auth = useAuthInfo()
    const {data: captchaData, refetch: refetchCaptcha} = useQueryCaptcha();
    const captcha = captchaData?.data
    const onSubmit = async (values: SigninForm) => {
        const ok = await signin({
            ...values,
            password: md5(values.password),
            captchaId: captcha!.captchaId,
        });
        if (!ok) {
            await refetchCaptcha()
        }
    };

    useEffect(() => {
        if (token) {
            router.replace(auth?.role?.route ?? "");
        }
    }, [token, router, auth]);

    return (
        <AppTheme
            themeComponents={{...inputsCustomizations}}
        >
            <CssBaseline enableColorScheme/>
            <SigninContainer direction="column" justifyContent="space-between">
                <ColorModeSelect
                    sx={{position: "fixed", top: "1rem", right: "1rem"}}
                />
                <CardContainer variant="outlined">
                    <Stack
                        direction={"row"}
                        spacing={2}
                        alignContent={"center"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                    >
                        <Box
                            component={"img"}
                            height={48}
                            src={"https://oss.kedaip.cn/keda/assets/logos.png"}
                        />
                        <Typography
                            component="h2"
                            variant="h2"
                            sx={{width: "100%", fontSize: "clamp(1.8rem, 10vw, 2.15rem)"}}
                        >
                            欢迎登录
                        </Typography>
                    </Stack>
                    <Box sx={{display: "flex", mt: 1, flexDirection: "row", gap: 2}}>
                        <Button
                            fullWidth
                            variant="outlined"
                            sx={{letterSpacing: 4}}
                            startIcon={<CropFreeOutlined/>}
                        >
                            扫码登录
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            sx={{letterSpacing: 4}}
                            startIcon={<EmailOutlined/>}
                        >
                            免密登录
                        </Button>
                    </Box>
                    <Divider>或者</Divider>
                    <Box
                        component="form"
                        noValidate
                        onSubmit={handleSubmit(onSubmit)}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            gap: 2,
                        }}
                    >
                        <FormControl required={true}>
                            <FormLabel htmlFor="account">账号</FormLabel>
                            <TextField
                                error={!!errors?.account}
                                helperText={errors?.account?.message}
                                {...register("account")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 12},
                                        endAdornment: <AccountCircleOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="请输入登录账号"
                                autoFocus
                                required
                                fullWidth
                                variant="outlined"
                            />
                        </FormControl>
                        <FormControl required={true}>
                            <FormLabel htmlFor="password">密码</FormLabel>
                            <TextField
                                error={!!errors?.password}
                                helperText={errors?.password?.message}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 20},
                                        endAdornment: <PasswordOutlined color="disabled"/>,
                                    },
                                }}
                                {...register("password")}
                                placeholder="请填写登录密码"
                                type="password"
                                required={true}
                                fullWidth
                                variant="outlined"
                            />
                        </FormControl>
                        <FormControl required={true}>
                            <FormLabel htmlFor="captcha">验证码</FormLabel>
                            <TextField
                                error={!!errors?.captcha}
                                helperText={errors?.captcha?.message}
                                {...register("captcha")}
                                placeholder="请填写验证码"
                                required
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 6},
                                        endAdornment: (
                                            <Box
                                                component={"img"}
                                                sx={{cursor: "pointer"}}
                                                onClick={() => refetchCaptcha()}
                                                width={80}
                                                height={40}
                                                src={captcha?.picPath}
                                            />
                                        ),
                                    },
                                }}
                                fullWidth
                                variant="outlined"
                            />
                        </FormControl>
                        <Stack direction="row" justifyContent="space-between">
                            <FormControlLabel
                                control={<Checkbox value="remember" color="primary"/>}
                                label="记住密码"
                            />
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                sx={{alignSelf: "center"}}
                            >
                                忘记密码?
                            </Link>
                        </Stack>
                        <ForgotPassword handleClose={() => {
                        }} open={false}/>
                        <Button disabled={signinPending} loading={signinPending} type="submit" fullWidth variant="contained">
                            登录
                        </Button>
                        <Typography sx={{textAlign: "center", letterSpacing: 3}}>
                            还没有账号?
                            <Link
                                variant="body2"
                                sx={{alignSelf: "center"}}
                            >
                                立即注册
                            </Link>
                        </Typography>
                    </Box>
                </CardContainer>
            </SigninContainer>
        </AppTheme>
    );
}
